import { useEffect, useState } from "react";
import PropTypes from "prop-types";

import ApplicationBlock from "./ApplicationBlock";

import { useAeApp } from "../utils/hooks/useAeApp";
import { useBusApp } from "../utils/hooks/useBusApp";
import { useEcheckinApp } from "../utils/hooks/useEcheckinApp";
import { useOeApp } from "../utils/hooks/useOeApp";
import { useEprApp } from "../utils/hooks/useEprApp";
// import { useSurveyApp } from "../utils/hooks/useSurveyApp";

/**
 * Display all relevant applications for the selected student
 * @name StudentApplications
 * @param {bool} expand
 * @param {bool} isGuardian
 * @param {string} lang
 * @param {bool} loader
 * @param {func} setExpand
 * @param {func} setLoader
 * @param {{}} student
 * @returns {JSX.Element}
 */
const StudentApplications = ({ expand, isGuardian, lang, loader, setExpand, setLoader, student }) => {
    const { appObj: oeApp, loading: oeLoading } = useOeApp(student);
    const { appObj: echeckinApp, loading: echeckinLoading } = useEcheckinApp(student);
    const { appObj: eprApp, loading: eprLoading } = useEprApp(student);
    const { appObj: aeApp, loading: aeLoading } = useAeApp(student);
    const { appObj: busApp, loading: busLoading } = useBusApp(isGuardian, student);
    /* we may need to use the following hook in the future */
    // const { appObj: surveyApp, loading: surveyLoading } = useSurveyApp(lang, student);

    const [noApps, setNoApps] = useState(false);

    /**
     * If all apps are finished loading, remove the page loader
     */
    useEffect(() => {
        if (!oeLoading && !echeckinLoading && !eprLoading && !aeLoading && !busLoading) {
            setLoader(false);
        }
    }, [aeLoading, busLoading, echeckinLoading, eprLoading, oeLoading, setLoader]);

    /**
     * If there are no apps to display, display a message
     */
    useEffect(() => {
        if ((oeApp || echeckinApp || eprApp || aeApp || busApp) && !loader) {
            setNoApps(false);
        } else {
            setNoApps(true);
        }
    }, [aeApp, busApp, echeckinApp, eprApp, loader, oeApp]);

    if (noApps && !loader) {
        const noApp = {
            app: "NOAPP",
            body: "NoApp_Body1",
            buttons: null,
            endDate: null,
            icon: "DCSD",
            moreInfo: {},
            startDate: null,
            status: "NoApp_Closed",
            statusColor: "#0669B3", // pearl blue
            subBody: "NoApp_Body2",
            title: "NoApp_Header"
        };
        return (
            <div>
                <ApplicationBlock application={noApp} lang={lang} />
            </div>
        );
    }

    return (
        <div>
            {oeApp && !oeLoading && (
                <ApplicationBlock
                    application={oeApp}
                    expand={expand}
                    key={`app-block-${student?.key || Math.floor(Math.random() * 100)}-oe`}
                    lang={lang}
                    setExpand={setExpand}
                    student={student}
                />
            )}
            {echeckinApp && !echeckinLoading && (
                <ApplicationBlock
                    application={echeckinApp}
                    key={`app-block-${student?.key || Math.floor(Math.random() * 100)}-echeckin`}
                    lang={lang}
                />
            )}
            {eprApp && !eprLoading && (
                <ApplicationBlock
                    application={eprApp}
                    key={`app-block-${student?.key || Math.floor(Math.random() * 100)}-epr`}
                    lang={lang}
                    setExpand={setExpand}
                />
            )}
            {aeApp && !aeLoading && (
                <ApplicationBlock
                    application={aeApp}
                    key={`app-block-${student?.key || Math.floor(Math.random() * 100)}-ae`}
                    lang={lang}
                />
            )}
            {busApp && !busLoading && (
                <ApplicationBlock
                    application={busApp}
                    key={`app-block-${student?.key || Math.floor(Math.random() * 100)}-bus`}
                    lang={lang}
                />
            )}
            {/*
            SurveyApp is Echeckin Based, and is almost completely built - we may need this in the future if/when we
            need another survey
            */}
            {/* {surveyApp && (
                <ApplicationBlock
                    application={surveyApp}
                    expand={expand}
                    key={`app-block-${student?.key || Math.floor(Math.random() * 100)}-survey`}
                    lang={lang}
                    setExpand={setExpand}
                    student={student}
                />
            )} */}
        </div>
    );
};

StudentApplications.propTypes = {
    expand: PropTypes.bool.isRequired,
    isGuardian: PropTypes.bool,
    lang: PropTypes.string,
    loader: PropTypes.bool,
    setExpand: PropTypes.func.isRequired,
    setLoader: PropTypes.func.isRequired,
    student: PropTypes.oneOfType([PropTypes.object])
};

StudentApplications.defaultProps = {
    isGuardian: false,
    loader: true,
    lang: "en",
    student: null
};

export default StudentApplications;
